import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Package, ShoppingBag, Users, FileSpreadsheet, Plus, Settings, BarChart3, Upload, Loader2, Pencil, Trash2, Image, Heart, Tag, Megaphone, Layout } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORDER_STATUSES = ['pending_payment','paid','accepted','in_progress','packed','out_for_delivery','delivered','completed','cancelled'];
const STATUS_COLORS = {
  pending_payment: 'bg-[#F9E4A6]/30 text-[#8B7A2B]', paid: 'bg-[#C5DB7B]/20 text-[#D4859B]',
  accepted: 'bg-[#C3B1E1]/20 text-[#7B5EA7]', in_progress: 'bg-[#F4D5C9]/40 text-[#B07C5F]',
  packed: 'bg-[#DDA7A5]/20 text-[#B07C5F]', out_for_delivery: 'bg-[#C5DB7B]/30 text-[#D4859B]',
  delivered: 'bg-[#C5DB7B]/20 text-[#D4859B]', completed: 'bg-[#D4859B]/10 text-[#D4859B]', cancelled: 'bg-red-50 text-red-600',
};

export default function AdminDashboard() {
  const { t, lang } = useLanguage();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [inventory, setInventory] = useState({ snapshots: [], flowers: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (!isAdmin) { navigate('/'); return; }
    fetchData();
  }, [user, isAdmin, authLoading, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, ordersRes, productsRes, invRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { withCredentials: true }),
        axios.get(`${API}/admin/orders`, { withCredentials: true }),
        axios.get(`${API}/products?limit=100`),
        axios.get(`${API}/admin/inventory`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data.orders);
      setProducts(productsRes.data.products);
      setInventory(invRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const formatPrice = (p) => new Intl.NumberFormat('id-ID').format(p);
  const formatDate = (d) => new Date(d).toLocaleDateString(lang === 'id' ? 'id-ID' : 'en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#C5DB7B]" size={24} /></div>;
  }

  return (
    <div className="min-h-screen pb-20 md:pb-0" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 md:px-12 py-6 md:py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-light text-[#2C2A29]">Admin Dashboard</h1>
            <p className="text-xs text-[#6B655F] font-light mt-1">{lang === 'id' ? 'Kelola toko Seikat Anda' : 'Manage your Seikat store'}</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto no-scrollbar -mx-4 px-4 md:mx-0 md:px-0 mb-6">
            <TabsList className="glass-card-strong rounded-2xl p-1.5 inline-flex">
              {[
                { val: 'dashboard', icon: BarChart3, label: t('admin.dashboard') },
                { val: 'orders', icon: ShoppingBag, label: t('admin.orders') },
                { val: 'products', icon: Package, label: t('admin.products') },
                { val: 'analytics', icon: BarChart3, label: lang === 'id' ? 'Analitik' : 'Analytics' },
                { val: 'promos', icon: Tag, label: lang === 'id' ? 'Promo' : 'Promos' },
                { val: 'banners', icon: Megaphone, label: 'Banners' },
                { val: 'inventory', icon: FileSpreadsheet, label: t('admin.inventory') },
                { val: 'gallery', icon: Image, label: t('nav.gallery') },
                { val: 'cms', icon: Layout, label: 'CMS' },
                { val: 'settings', icon: Settings, label: t('admin.settings') },
              ].map(tab => (
                <TabsTrigger key={tab.val} value={tab.val} className="rounded-xl text-xs data-[state=active]:bg-[#D4859B] data-[state=active]:text-white data-[state=active]:shadow-lg px-3 py-2" data-testid={`tab-${tab.val}`}>
                  <tab.icon size={13} className="mr-1.5" /> {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <TabsContent value="dashboard"><DashboardTab stats={stats} t={t} lang={lang} formatPrice={formatPrice} formatDate={formatDate} /></TabsContent>
          <TabsContent value="orders"><OrdersTab orders={orders} t={t} lang={lang} formatPrice={formatPrice} formatDate={formatDate} onRefresh={fetchData} /></TabsContent>
          <TabsContent value="products"><ProductsTab products={products} t={t} lang={lang} formatPrice={formatPrice} onRefresh={fetchData} /></TabsContent>
          <TabsContent value="analytics"><AnalyticsTab t={t} lang={lang} formatPrice={formatPrice} /></TabsContent>
          <TabsContent value="promos"><PromosTab t={t} lang={lang} formatPrice={formatPrice} /></TabsContent>
          <TabsContent value="banners"><BannersTab t={t} lang={lang} /></TabsContent>
          <TabsContent value="inventory"><InventoryTab inventory={inventory} t={t} lang={lang} onRefresh={fetchData} /></TabsContent>
          <TabsContent value="gallery"><GalleryTab t={t} lang={lang} /></TabsContent>
          <TabsContent value="cms"><CMSTab t={t} lang={lang} /></TabsContent>
          <TabsContent value="settings"><SettingsTab t={t} lang={lang} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function DashboardTab({ stats, t, lang, formatPrice, formatDate }) {
  if (!stats) return null;
  const cards = [
    { label: t('admin.total_orders'), value: stats.total_orders, icon: ShoppingBag, gradient: 'from-[#D4859B] to-[#6B8A5E]' },
    { label: t('admin.pending_orders'), value: stats.pending_orders, icon: Package, gradient: 'from-[#DDA7A5] to-[#E8C4C0]' },
    { label: t('admin.paid_orders'), value: stats.paid_orders, icon: ShoppingBag, gradient: 'from-[#C5DB7B] to-[#B5C9A3]' },
    { label: t('admin.total_products'), value: stats.total_products, icon: Package, gradient: 'from-[#C3B1E1] to-[#D8CCEE]' },
    { label: t('admin.total_users'), value: stats.total_users, icon: Users, gradient: 'from-[#F4D5C9] to-[#F9E4A6]' },
  ];

  return (
    <div data-testid="admin-dashboard-content">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 mb-6">
        {cards.map((card, i) => (
          <div key={i} className="glass-card rounded-2xl p-4 md:p-5 group hover:scale-[1.02] transition-all" data-testid={`stat-${i}`}>
            <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${card.gradient} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
              <card.icon size={16} className="text-white" strokeWidth={1.5} />
            </div>
            <p className="text-2xl font-semibold text-[#2C2A29]">{card.value}</p>
            <span className="text-[10px] text-[#6B655F] font-medium uppercase tracking-wider">{card.label}</span>
          </div>
        ))}
      </div>
      <div className="glass-card rounded-2xl p-5">
        <h3 className="text-sm font-medium text-[#2C2A29] mb-4">{t('admin.recent_orders')}</h3>
        <Table>
          <TableHeader><TableRow>
            <TableHead className="text-[10px] font-medium uppercase tracking-wider">Order ID</TableHead>
            <TableHead className="text-[10px] font-medium uppercase tracking-wider">Status</TableHead>
            <TableHead className="text-[10px] font-medium uppercase tracking-wider">Total</TableHead>
            <TableHead className="text-[10px] font-medium uppercase tracking-wider">{t('orders.date')}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(stats.recent_orders || []).map(order => (
              <TableRow key={order.order_id}>
                <TableCell className="text-xs font-mono">{order.order_id?.slice(0, 16)}...</TableCell>
                <TableCell><Badge className={`${STATUS_COLORS[order.status] || ''} border-0 rounded-full text-[10px]`}>{t(`orders.status_${order.status}`)}</Badge></TableCell>
                <TableCell className="text-xs font-medium">Rp {formatPrice(order.total)}</TableCell>
                <TableCell className="text-xs text-[#6B655F]">{formatDate(order.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function OrdersTab({ orders, t, lang, formatPrice, formatDate, onRefresh }) {
  const [statusFilter, setStatusFilter] = useState('');

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(lang === 'id' ? 'Status diperbarui' : 'Status updated');
      onRefresh();
    } catch { toast.error('Error'); }
  };

  const filtered = statusFilter && statusFilter !== 'all' ? orders.filter(o => o.status === statusFilter) : orders;

  return (
    <div data-testid="admin-orders">
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-5 -mx-4 px-4 md:mx-0 md:px-0">
        {['all', ...ORDER_STATUSES].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] font-medium capitalize transition-all ${
              (statusFilter || 'all') === s ? 'bg-[#D4859B] text-white shadow' : 'glass-card-subtle text-[#6B655F]'
            }`} data-testid={`order-filter-${s}`}>
            {s === 'all' ? (lang === 'id' ? 'Semua' : 'All') : t(`orders.status_${s}`)}
          </button>
        ))}
      </div>
      <div className="glass-card rounded-2xl overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="text-[10px] font-medium uppercase tracking-wider">Order</TableHead>
            <TableHead className="text-[10px] font-medium uppercase tracking-wider">{lang === 'id' ? 'Penerima' : 'To'}</TableHead>
            <TableHead className="text-[10px] font-medium uppercase tracking-wider">Status</TableHead>
            <TableHead className="text-[10px] font-medium uppercase tracking-wider">Total</TableHead>
            <TableHead className="text-[10px] font-medium uppercase tracking-wider">{t('admin.update_status')}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map(order => (
              <TableRow key={order.order_id} data-testid={`admin-order-${order.order_id}`}>
                <TableCell><span className="text-xs font-mono">{order.order_id?.slice(0, 12)}...</span><br/><span className="text-[10px] text-[#6B655F]">{formatDate(order.created_at)}</span></TableCell>
                <TableCell className="text-xs">{order.delivery_name}</TableCell>
                <TableCell><Badge className={`${STATUS_COLORS[order.status] || ''} border-0 rounded-full text-[10px]`}>{t(`orders.status_${order.status}`)}</Badge></TableCell>
                <TableCell className="text-xs font-medium">Rp {formatPrice(order.total)}</TableCell>
                <TableCell>
                  <Select value="" onValueChange={(val) => handleStatusChange(order.order_id, val)}>
                    <SelectTrigger className="h-8 text-[10px] w-32 rounded-xl glass-card-subtle border-white/40" data-testid={`update-status-${order.order_id}`}>
                      <SelectValue placeholder={t('admin.update_status')} />
                    </SelectTrigger>
                    <SelectContent>{ORDER_STATUSES.map(s => (<SelectItem key={s} value={s} className="text-xs">{t(`orders.status_${s}`)}</SelectItem>))}</SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ProductsTab({ products, t, lang, formatPrice, onRefresh }) {
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const emptyForm = { name_en: '', name_id: '', description_en: '', description_id: '', story_en: '', story_id: '', price: '', category: 'bouquet', occasion: [], style: '', size: 'medium', images: [''], is_memory_bouquet: false, is_featured: false };
  const [form, setForm] = useState(emptyForm);

  const openAdd = () => { setEditingProduct(null); setForm(emptyForm); setShowDialog(true); };
  const openEdit = (p) => {
    setEditingProduct(p);
    setForm({ name_en: p.name_en || '', name_id: p.name_id || '', description_en: p.description_en || '', description_id: p.description_id || '',
      story_en: p.story_en || '', story_id: p.story_id || '', price: String(p.price || ''), category: p.category || 'bouquet',
      occasion: p.occasion || [], style: p.style || '', size: p.size || 'medium', images: p.images?.length ? p.images : [''],
      is_memory_bouquet: p.is_memory_bouquet || false, is_featured: p.is_featured || false });
    setShowDialog(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0, images: form.images.filter(Boolean) };
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.product_id}`, payload, { withCredentials: true });
        toast.success(lang === 'id' ? 'Produk diperbarui!' : 'Product updated!');
      } else {
        await axios.post(`${API}/admin/products`, payload, { withCredentials: true });
        toast.success(lang === 'id' ? 'Produk ditambahkan!' : 'Product added!');
      }
      setShowDialog(false);
      onRefresh();
    } catch { toast.error('Error'); }
    setSaving(false);
  };

  const handleArchive = async (productId) => {
    try {
      await axios.delete(`${API}/admin/products/${productId}`, { withCredentials: true });
      toast.success(lang === 'id' ? 'Produk diarsipkan' : 'Product archived');
      onRefresh();
    } catch { toast.error('Error'); }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex justify-between items-center mb-5">
        <p className="text-xs text-[#6B655F]">{products.length} {lang === 'id' ? 'produk' : 'products'}</p>
        <Button onClick={openAdd} className="bg-[#D4859B] hover:bg-[#C07088] text-white rounded-full text-xs shadow-lg shadow-[#D4859B]/15 btn-glow" data-testid="add-product-btn">
          <Plus size={14} className="mr-1.5" /> {t('admin.add_product')}
        </Button>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto glass-card-strong rounded-3xl border-white/50">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-light">
              {editingProduct ? t('admin.edit_product') : t('admin.add_product')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Name (EN)</label><Input value={form.name_en} onChange={e => setForm({...form, name_en: e.target.value})} className="rounded-xl glass-input border-white/50" data-testid="product-name-en" /></div>
              <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Name (ID)</label><Input value={form.name_id} onChange={e => setForm({...form, name_id: e.target.value})} className="rounded-xl glass-input border-white/50" data-testid="product-name-id" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Description (EN)</label><Textarea value={form.description_en} onChange={e => setForm({...form, description_en: e.target.value})} className="rounded-xl glass-input border-white/50" rows={2} /></div>
              <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Description (ID)</label><Textarea value={form.description_id} onChange={e => setForm({...form, description_id: e.target.value})} className="rounded-xl glass-input border-white/50" rows={2} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Story (EN)</label><Textarea value={form.story_en} onChange={e => setForm({...form, story_en: e.target.value})} className="rounded-xl glass-input border-white/50" rows={2} /></div>
              <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Story (ID)</label><Textarea value={form.story_id} onChange={e => setForm({...form, story_id: e.target.value})} className="rounded-xl glass-input border-white/50" rows={2} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">{lang === 'id' ? 'Harga (Rp)' : 'Price (Rp)'}</label><Input type="number" value={form.price} onChange={e => setForm({...form, price: e.target.value})} className="rounded-xl glass-input border-white/50" data-testid="product-price" /></div>
              <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Style</label><Input value={form.style} onChange={e => setForm({...form, style: e.target.value})} className="rounded-xl glass-input border-white/50" /></div>
              <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Image URL</label><Input value={form.images[0]} onChange={e => setForm({...form, images: [e.target.value]})} className="rounded-xl glass-input border-white/50" /></div>
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={form.is_memory_bouquet} onChange={e => setForm({...form, is_memory_bouquet: e.target.checked})} className="rounded" /> Memory Bouquet</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={form.is_featured} onChange={e => setForm({...form, is_featured: e.target.checked})} className="rounded" /> Featured</label>
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-[#D4859B] hover:bg-[#C07088] text-white rounded-full py-5 shadow-lg btn-glow" data-testid="save-product-btn">
              {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : null} {t('common.save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="glass-card rounded-2xl overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="text-[10px] font-medium uppercase tracking-wider">{lang === 'id' ? 'Produk' : 'Product'}</TableHead>
            <TableHead className="text-[10px] font-medium uppercase tracking-wider">{lang === 'id' ? 'Harga' : 'Price'}</TableHead>
            <TableHead className="text-[10px] font-medium uppercase tracking-wider">Type</TableHead>
            <TableHead className="text-[10px] font-medium uppercase tracking-wider">{lang === 'id' ? 'Aksi' : 'Actions'}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {products.map(p => (
              <TableRow key={p.product_id} data-testid={`admin-product-${p.product_id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {p.images?.[0] && <img src={p.images[0]} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                    <div><p className="text-sm font-medium">{lang === 'id' ? p.name_id : p.name_en}</p><p className="text-[10px] text-[#6B655F] capitalize">{p.style}</p></div>
                  </div>
                </TableCell>
                <TableCell className="text-sm font-medium">Rp {formatPrice(p.price)}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {p.is_memory_bouquet && <Badge className="bg-[#DDA7A5]/15 text-[#D4859B] border-0 rounded-full text-[9px]">Memory</Badge>}
                    {p.is_featured && <Badge className="bg-[#F9E4A6]/30 text-[#8B7A2B] border-0 rounded-full text-[9px]">Featured</Badge>}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(p)} className="p-2 rounded-xl hover:bg-white/50 text-[#6B655F] transition-all" data-testid={`edit-product-${p.product_id}`}><Pencil size={14} /></button>
                    <button onClick={() => handleArchive(p.product_id)} className="p-2 rounded-xl hover:bg-red-50 text-[#6B655F] hover:text-red-500 transition-all" data-testid={`archive-product-${p.product_id}`}><Trash2 size={14} /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function InventoryTab({ inventory, t, lang, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/inventory/import`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${res.data.items_imported} items imported!`);
      onRefresh();
    } catch { toast.error(lang === 'id' ? 'Gagal mengimpor' : 'Import failed'); }
    setUploading(false);
  };

  return (
    <div data-testid="admin-inventory">
      <div className="flex items-center justify-between mb-5">
        <p className="text-xs text-[#6B655F]">{inventory.flowers.length} {lang === 'id' ? 'bunga' : 'flowers'}</p>
        <div>
          <input type="file" ref={fileRef} onChange={handleUpload} className="hidden" accept=".xlsx,.xls" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#D4859B] hover:bg-[#C07088] text-white rounded-full text-xs shadow-lg btn-glow" data-testid="import-excel-btn">
            {uploading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Upload size={14} className="mr-1.5" />} {t('admin.upload_excel')}
          </Button>
        </div>
      </div>
      <div className="glass-card rounded-2xl overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="text-[10px] font-medium uppercase tracking-wider">Code</TableHead>
            <TableHead className="text-[10px] font-medium uppercase tracking-wider">{lang === 'id' ? 'Nama' : 'Name'}</TableHead>
            <TableHead className="text-[10px] font-medium uppercase tracking-wider">{lang === 'id' ? 'Stok' : 'Qty'}</TableHead>
            <TableHead className="text-[10px] font-medium uppercase tracking-wider">Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {inventory.flowers.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center text-sm text-[#6B655F] py-10">{lang === 'id' ? 'Belum ada data. Impor file Excel.' : 'No data yet. Import an Excel file.'}</TableCell></TableRow>
            ) : inventory.flowers.map((f, i) => (
              <TableRow key={i}>
                <TableCell className="text-xs font-mono">{f.flower_code}</TableCell>
                <TableCell className="text-sm">{f.flower_name}</TableCell>
                <TableCell className="text-sm font-medium">{f.qty}</TableCell>
                <TableCell><Badge className={`${f.qty > 0 ? 'bg-[#C5DB7B]/20 text-[#D4859B]' : 'bg-red-50 text-red-600'} border-0 rounded-full text-[10px]`}>{f.qty > 0 ? 'available' : 'out'}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function GalleryTab({ t, lang }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title_en: '', title_id: '', image: '', category: 'romantic', caption_en: '', caption_id: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchGallery(); }, []);

  const fetchGallery = async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/gallery`); setItems(res.data.items); } catch {}
    setLoading(false);
  };

  const handleAdd = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/gallery`, { ...form, status: 'active' }, { withCredentials: true });
      toast.success(lang === 'id' ? 'Item galeri ditambahkan!' : 'Gallery item added!');
      setShowAdd(false); setForm({ title_en: '', title_id: '', image: '', category: 'romantic', caption_en: '', caption_id: '' });
      fetchGallery();
    } catch { toast.error('Error'); }
    setSaving(false);
  };

  const handleDelete = async (itemId) => {
    try {
      await axios.delete(`${API}/admin/gallery/${itemId}`, { withCredentials: true });
      toast.success(lang === 'id' ? 'Item dihapus' : 'Item removed');
      fetchGallery();
    } catch { toast.error('Error'); }
  };

  return (
    <div data-testid="admin-gallery">
      <div className="flex justify-between items-center mb-5">
        <p className="text-xs text-[#6B655F]">{items.length} {lang === 'id' ? 'item galeri' : 'gallery items'}</p>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4859B] hover:bg-[#C07088] text-white rounded-full text-xs shadow-lg btn-glow" data-testid="add-gallery-btn">
              <Plus size={14} className="mr-1.5" /> {lang === 'id' ? 'Tambah Item' : 'Add Item'}
            </Button>
          </DialogTrigger>
          <DialogContent className="glass-card-strong rounded-3xl border-white/50">
            <DialogHeader><DialogTitle className="font-['Cormorant_Garamond'] text-xl">{lang === 'id' ? 'Tambah Item Galeri' : 'Add Gallery Item'}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Title (EN)</label><Input value={form.title_en} onChange={e => setForm({...form, title_en: e.target.value})} className="rounded-xl glass-input border-white/50" /></div>
                <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Title (ID)</label><Input value={form.title_id} onChange={e => setForm({...form, title_id: e.target.value})} className="rounded-xl glass-input border-white/50" /></div>
              </div>
              <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Image URL</label><Input value={form.image} onChange={e => setForm({...form, image: e.target.value})} className="rounded-xl glass-input border-white/50" /></div>
              <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Category</label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                  <SelectTrigger className="rounded-xl glass-input border-white/50"><SelectValue /></SelectTrigger>
                  <SelectContent>{['romantic','seasonal','elegant','celebration','behind_scenes'].map(c => (<SelectItem key={c} value={c} className="capitalize">{c.replace('_', ' ')}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <Button onClick={handleAdd} disabled={saving} className="w-full bg-[#D4859B] text-white rounded-full btn-glow">{saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}{t('common.save')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {loading ? <div className="text-center py-10 text-[#6B655F]">{t('common.loading')}</div> : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map(item => (
            <div key={item.item_id} className="glass-card rounded-2xl overflow-hidden group" data-testid={`admin-gallery-${item.item_id}`}>
              <div className="aspect-[4/3] overflow-hidden relative">
                <img src={item.image} alt="" className="w-full h-full object-cover" />
                <button onClick={() => handleDelete(item.item_id)} className="absolute top-2 right-2 p-2 rounded-xl glass-card-strong opacity-0 group-hover:opacity-100 transition-all hover:bg-red-50" data-testid={`delete-gallery-${item.item_id}`}>
                  <Trash2 size={14} className="text-red-500" />
                </button>
              </div>
              <div className="p-3">
                <p className="text-xs font-medium truncate">{lang === 'id' ? item.title_id : item.title_en}</p>
                <p className="text-[10px] text-[#6B655F] capitalize">{item.category?.replace('_', ' ')}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsTab({ t, lang }) {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { axios.get(`${API}/settings/bank-transfer`).then(r => setConfig(r.data)).catch(() => {}); }, []);

  const handleSave = async () => {
    setSaving(true);
    try { await axios.put(`${API}/admin/settings/bank-transfer`, config, { withCredentials: true }); toast.success(lang === 'id' ? 'Tersimpan!' : 'Saved!'); }
    catch { toast.error('Error'); }
    setSaving(false);
  };

  if (!config) return null;

  return (
    <div data-testid="admin-settings">
      <div className="glass-card rounded-2xl p-6 max-w-lg">
        <h3 className="text-sm font-medium text-[#2C2A29] mb-5">{t('admin.bank_settings')}</h3>
        <div className="space-y-3">
          {[
            { key: 'bank_name', label: 'Bank Name' },
            { key: 'account_number', label: lang === 'id' ? 'No. Rekening' : 'Account Number' },
            { key: 'account_name', label: lang === 'id' ? 'Atas Nama' : 'Account Name' },
          ].map(field => (
            <div key={field.key}><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">{field.label}</label>
              <Input value={config[field.key] || ''} onChange={e => setConfig({...config, [field.key]: e.target.value})} className="rounded-xl glass-input border-white/50" data-testid={`${field.key.replace('_', '-')}-input`} />
            </div>
          ))}
          <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">{lang === 'id' ? 'Ongkos Kirim (Rp)' : 'Delivery Fee (Rp)'}</label>
            <Input type="number" value={config.delivery_fee || 0} onChange={e => setConfig({...config, delivery_fee: parseInt(e.target.value) || 0})} className="rounded-xl glass-input border-white/50" data-testid="delivery-fee-input" />
          </div>
          <Button onClick={handleSave} disabled={saving} className="w-full bg-[#D4859B] text-white rounded-full shadow-lg btn-glow" data-testid="save-bank-settings">
            {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : null} {t('common.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}


function AnalyticsTab({ t, lang, formatPrice }) {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/analytics?period=${period}`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period]);

  if (loading) return <div className="text-center py-10"><Loader2 className="animate-spin mx-auto text-[#C5DB7B]" size={24} /></div>;
  if (!data) return null;

  const rev = data.revenue || {};

  return (
    <div data-testid="admin-analytics">
      <div className="flex gap-2 mb-6">
        {['7d', '30d', '90d'].map(p => (
          <button key={p} onClick={() => setPeriod(p)} className={`px-4 py-2 rounded-xl text-xs font-medium transition-all ${period === p ? 'bg-[#D4859B] text-white shadow' : 'glass-card-subtle text-[#6B655F]'}`} data-testid={`period-${p}`}>
            {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: lang === 'id' ? 'Total Revenue' : 'Total Revenue', value: `Rp ${formatPrice(rev.total_revenue || 0)}`, gradient: 'from-[#D4859B] to-[#6B8A5E]' },
          { label: lang === 'id' ? 'Pesanan Terbayar' : 'Paid Orders', value: rev.order_count || 0, gradient: 'from-[#C5DB7B] to-[#B5C9A3]' },
          { label: lang === 'id' ? 'Rata-rata' : 'Avg Order', value: `Rp ${formatPrice(Math.round(rev.avg_order || 0))}`, gradient: 'from-[#DDA7A5] to-[#E8C4C0]' },
          { label: 'Memory Pages', value: data.total_memory_pages || 0, gradient: 'from-[#C3B1E1] to-[#D8CCEE]' },
        ].map((card, i) => (
          <div key={i} className="glass-card rounded-2xl p-5" data-testid={`analytics-stat-${i}`}>
            <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${card.gradient} mb-3`} />
            <p className="text-xl font-semibold text-[#2C2A29]">{card.value}</p>
            <span className="text-[10px] text-[#6B655F] font-medium uppercase tracking-wider">{card.label}</span>
          </div>
        ))}
      </div>

      {/* Top Products */}
      {data.top_products?.length > 0 && (
        <div className="glass-card rounded-2xl p-5 mb-6">
          <h3 className="text-sm font-medium text-[#2C2A29] mb-4">{lang === 'id' ? 'Produk Terlaris' : 'Top Products'}</h3>
          <div className="space-y-3">
            {data.top_products.map((p, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-[#C5DB7B] w-5">{i + 1}</span>
                  <span className="text-sm text-[#2C2A29]">{p.name || p.product_id}</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-[#2C2A29]">Rp {formatPrice(p.revenue || 0)}</span>
                  <span className="text-[10px] text-[#6B655F] ml-2">({p.quantity}x)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Daily Chart (simple bar representation) */}
      {data.daily_chart?.length > 0 && (
        <div className="glass-card rounded-2xl p-5">
          <h3 className="text-sm font-medium text-[#2C2A29] mb-4">{lang === 'id' ? 'Revenue Harian' : 'Daily Revenue'}</h3>
          <div className="flex items-end gap-1 h-32">
            {data.daily_chart.map((d, i) => {
              const maxRev = Math.max(...data.daily_chart.map(x => x.revenue));
              const height = maxRev > 0 ? (d.revenue / maxRev) * 100 : 0;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: Rp ${formatPrice(d.revenue)}`}>
                  <div className="w-full rounded-t-md bg-gradient-to-t from-[#C5DB7B] to-[#B5C9A3] transition-all" style={{ height: `${Math.max(height, 4)}%` }} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function PromosTab({ t, lang, formatPrice }) {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ code: '', discount_type: 'percentage', discount_value: 10, min_order: 0, max_discount: 0, usage_limit: 0, valid_until: '' });

  useEffect(() => { fetchPromos(); }, []);
  const fetchPromos = () => {
    setLoading(true);
    axios.get(`${API}/admin/promo-codes`, { withCredentials: true }).then(r => setPromos(r.data.promo_codes)).catch(() => {}).finally(() => setLoading(false));
  };

  const handleCreate = async () => {
    if (!form.code.trim()) { toast.error('Code is required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/promo-codes`, { ...form, discount_value: parseFloat(form.discount_value), min_order: parseFloat(form.min_order) || 0, max_discount: parseFloat(form.max_discount) || 0, usage_limit: parseInt(form.usage_limit) || 0 }, { withCredentials: true });
      toast.success(lang === 'id' ? 'Kode promo dibuat!' : 'Promo code created!');
      setShowAdd(false); setForm({ code: '', discount_type: 'percentage', discount_value: 10, min_order: 0, max_discount: 0, usage_limit: 0, valid_until: '' });
      fetchPromos();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  const handleDeactivate = async (codeId) => {
    await axios.delete(`${API}/admin/promo-codes/${codeId}`, { withCredentials: true });
    toast.success(lang === 'id' ? 'Dinonaktifkan' : 'Deactivated');
    fetchPromos();
  };

  return (
    <div data-testid="admin-promos">
      <div className="flex justify-between items-center mb-5">
        <p className="text-xs text-[#6B655F]">{promos.length} {lang === 'id' ? 'kode promo' : 'promo codes'}</p>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4859B] hover:bg-[#C07088] text-white rounded-full text-xs shadow-lg btn-glow" data-testid="add-promo-btn"><Plus size={14} className="mr-1.5" /> {lang === 'id' ? 'Buat Kode' : 'Create Code'}</Button>
          </DialogTrigger>
          <DialogContent className="glass-card-strong rounded-3xl border-white/50">
            <DialogHeader><DialogTitle className="font-['Cormorant_Garamond'] text-xl">{lang === 'id' ? 'Buat Kode Promo' : 'Create Promo Code'}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-3">
              <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Code</label><Input value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})} placeholder="SEIKAT20" className="rounded-xl glass-input border-white/50 uppercase" data-testid="promo-code-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Type</label>
                  <Select value={form.discount_type} onValueChange={v => setForm({...form, discount_type: v})}><SelectTrigger className="rounded-xl glass-input border-white/50"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="percentage">Percentage (%)</SelectItem><SelectItem value="fixed">Fixed (Rp)</SelectItem></SelectContent></Select>
                </div>
                <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Value</label><Input type="number" value={form.discount_value} onChange={e => setForm({...form, discount_value: e.target.value})} className="rounded-xl glass-input border-white/50" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Min Order (Rp)</label><Input type="number" value={form.min_order} onChange={e => setForm({...form, min_order: e.target.value})} className="rounded-xl glass-input border-white/50" /></div>
                <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Max Discount (Rp)</label><Input type="number" value={form.max_discount} onChange={e => setForm({...form, max_discount: e.target.value})} className="rounded-xl glass-input border-white/50" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Usage Limit</label><Input type="number" value={form.usage_limit} onChange={e => setForm({...form, usage_limit: e.target.value})} placeholder="0 = unlimited" className="rounded-xl glass-input border-white/50" /></div>
                <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Valid Until</label><Input type="date" value={form.valid_until} onChange={e => setForm({...form, valid_until: e.target.value})} className="rounded-xl glass-input border-white/50" /></div>
              </div>
              <Button onClick={handleCreate} disabled={saving} className="w-full bg-[#D4859B] text-white rounded-full btn-glow" data-testid="create-promo-btn">{saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}{t('common.save')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {loading ? <div className="text-center py-10"><Loader2 className="animate-spin mx-auto" size={20} /></div> : (
        <div className="glass-card rounded-2xl overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="text-[10px] font-medium uppercase tracking-wider">Code</TableHead>
              <TableHead className="text-[10px] font-medium uppercase tracking-wider">{lang === 'id' ? 'Diskon' : 'Discount'}</TableHead>
              <TableHead className="text-[10px] font-medium uppercase tracking-wider">{lang === 'id' ? 'Digunakan' : 'Used'}</TableHead>
              <TableHead className="text-[10px] font-medium uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-[10px] font-medium uppercase tracking-wider">{lang === 'id' ? 'Aksi' : 'Action'}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {promos.map(p => (
                <TableRow key={p.code_id}>
                  <TableCell className="text-xs font-mono font-semibold">{p.code}</TableCell>
                  <TableCell className="text-xs">{p.discount_type === 'percentage' ? `${p.discount_value}%` : `Rp ${formatPrice(p.discount_value)}`}</TableCell>
                  <TableCell className="text-xs">{p.used_count || 0}{p.usage_limit ? `/${p.usage_limit}` : ''}</TableCell>
                  <TableCell><Badge className={`${p.status === 'active' ? 'bg-[#C5DB7B]/20 text-[#D4859B]' : 'bg-red-50 text-red-600'} border-0 rounded-full text-[10px]`}>{p.status}</Badge></TableCell>
                  <TableCell>{p.status === 'active' && <button onClick={() => handleDeactivate(p.code_id)} className="text-[10px] text-red-400 hover:text-red-600" data-testid={`deactivate-${p.code_id}`}>{lang === 'id' ? 'Nonaktifkan' : 'Deactivate'}</button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function BannersTab({ t, lang }) {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title_en: '', title_id: '', subtitle_en: '', subtitle_id: '', image: '', link: '', bg_color: '#F5EDE6', text_color: '#2C2A29', start_date: '', end_date: '' });

  useEffect(() => { fetchBanners(); }, []);
  const fetchBanners = () => {
    setLoading(true);
    axios.get(`${API}/admin/banners`, { withCredentials: true }).then(r => setBanners(r.data.banners)).catch(() => {}).finally(() => setLoading(false));
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/banners`, form, { withCredentials: true });
      toast.success(lang === 'id' ? 'Banner dibuat!' : 'Banner created!');
      setShowAdd(false); setForm({ title_en: '', title_id: '', subtitle_en: '', subtitle_id: '', image: '', link: '', bg_color: '#F5EDE6', text_color: '#2C2A29', start_date: '', end_date: '' });
      fetchBanners();
    } catch { toast.error('Error'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/admin/banners/${id}`, { withCredentials: true });
    toast.success(lang === 'id' ? 'Banner dihapus' : 'Banner removed');
    fetchBanners();
  };

  return (
    <div data-testid="admin-banners">
      <div className="flex justify-between items-center mb-5">
        <p className="text-xs text-[#6B655F]">{banners.length} banners</p>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4859B] hover:bg-[#C07088] text-white rounded-full text-xs shadow-lg btn-glow" data-testid="add-banner-btn"><Plus size={14} className="mr-1.5" /> {lang === 'id' ? 'Buat Banner' : 'Create Banner'}</Button>
          </DialogTrigger>
          <DialogContent className="glass-card-strong rounded-3xl border-white/50 max-w-lg">
            <DialogHeader><DialogTitle className="font-['Cormorant_Garamond'] text-xl">{lang === 'id' ? 'Buat Banner' : 'Create Banner'}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Title (EN)</label><Input value={form.title_en} onChange={e => setForm({...form, title_en: e.target.value})} className="rounded-xl glass-input border-white/50" /></div>
                <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Title (ID)</label><Input value={form.title_id} onChange={e => setForm({...form, title_id: e.target.value})} className="rounded-xl glass-input border-white/50" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Subtitle (EN)</label><Input value={form.subtitle_en} onChange={e => setForm({...form, subtitle_en: e.target.value})} className="rounded-xl glass-input border-white/50" /></div>
                <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Subtitle (ID)</label><Input value={form.subtitle_id} onChange={e => setForm({...form, subtitle_id: e.target.value})} className="rounded-xl glass-input border-white/50" /></div>
              </div>
              <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Image URL</label><Input value={form.image} onChange={e => setForm({...form, image: e.target.value})} className="rounded-xl glass-input border-white/50" /></div>
              <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Link (e.g. /catalog)</label><Input value={form.link} onChange={e => setForm({...form, link: e.target.value})} className="rounded-xl glass-input border-white/50" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">BG Color</label><Input type="color" value={form.bg_color} onChange={e => setForm({...form, bg_color: e.target.value})} className="rounded-xl h-10" /></div>
                <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Text Color</label><Input type="color" value={form.text_color} onChange={e => setForm({...form, text_color: e.target.value})} className="rounded-xl h-10" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">Start Date</label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="rounded-xl glass-input border-white/50" /></div>
                <div><label className="text-[10px] font-semibold uppercase tracking-wider text-[#6B655F] mb-1 block">End Date</label><Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="rounded-xl glass-input border-white/50" /></div>
              </div>
              <Button onClick={handleCreate} disabled={saving} className="w-full bg-[#D4859B] text-white rounded-full btn-glow" data-testid="create-banner-btn">{saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}{t('common.save')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {loading ? <div className="text-center py-10"><Loader2 className="animate-spin mx-auto" size={20} /></div> : (
        <div className="space-y-3">
          {banners.map(b => (
            <div key={b.banner_id} className="glass-card rounded-2xl p-4 flex items-center gap-4" data-testid={`banner-${b.banner_id}`}>
              <div className="w-16 h-12 rounded-xl flex-shrink-0" style={{ backgroundColor: b.bg_color }}>{b.image && <img src={b.image} alt="" className="w-full h-full object-cover rounded-xl" />}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#2C2A29] truncate">{lang === 'id' ? b.title_id : b.title_en}</p>
                <p className="text-[10px] text-[#6B655F]">{b.start_date || 'Always'} - {b.end_date || 'Forever'}</p>
              </div>
              <Badge className={`${b.status === 'active' ? 'bg-[#C5DB7B]/20 text-[#D4859B]' : 'bg-red-50 text-red-600'} border-0 rounded-full text-[10px]`}>{b.status}</Badge>
              {b.status === 'active' && <button onClick={() => handleDelete(b.banner_id)} className="p-2 rounded-xl hover:bg-red-50 text-[#6B655F] hover:text-red-500 transition-all"><Trash2 size={14} /></button>}
            </div>
          ))}
          {banners.length === 0 && <p className="text-center py-10 text-sm text-[#6B655F]">{lang === 'id' ? 'Belum ada banner' : 'No banners yet'}</p>}
        </div>
      )}
    </div>
  );
}

function CMSTab({ t, lang }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/cms/homepage`).then(r => setConfig(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleToggle = (id) => {
    setConfig(prev => ({
      ...prev,
      sections: prev.sections.map(s => s.id === id ? { ...s, enabled: !s.enabled } : s)
    }));
  };

  const handleMove = (idx, dir) => {
    const sections = [...config.sections];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= sections.length) return;
    [sections[idx].order, sections[newIdx].order] = [sections[newIdx].order, sections[idx].order];
    sections.sort((a, b) => a.order - b.order);
    setConfig({ ...config, sections });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/cms/homepage`, config, { withCredentials: true });
      toast.success(lang === 'id' ? 'Tersimpan!' : 'Saved!');
    } catch { toast.error('Error'); }
    setSaving(false);
  };

  if (loading) return <div className="text-center py-10"><Loader2 className="animate-spin mx-auto" size={20} /></div>;
  if (!config) return null;

  const sectionLabels = {
    hero: { en: 'Hero Banner', id: 'Banner Utama' },
    how_it_works: { en: 'How It Works', id: 'Cara Kerja' },
    featured_products: { en: 'Featured Products', id: 'Produk Unggulan' },
    collections: { en: 'Collections', id: 'Koleksi' },
    banners: { en: 'Campaign Banners', id: 'Banner Kampanye' },
    about: { en: 'About Section', id: 'Tentang' },
    testimonials: { en: 'Testimonials', id: 'Testimoni' },
  };

  return (
    <div data-testid="admin-cms">
      <div className="glass-card rounded-2xl p-6 max-w-lg">
        <h3 className="text-sm font-medium text-[#2C2A29] mb-2">{lang === 'id' ? 'Pengaturan Halaman Utama' : 'Homepage Sections'}</h3>
        <p className="text-xs text-[#6B655F] font-light mb-6">{lang === 'id' ? 'Aktifkan/nonaktifkan dan urutkan section homepage' : 'Toggle and reorder homepage sections'}</p>
        <div className="space-y-2">
          {config.sections.sort((a, b) => a.order - b.order).map((section, idx) => (
            <div key={section.id} className={`flex items-center gap-3 p-3 rounded-xl transition-all ${section.enabled ? 'glass-card-subtle' : 'bg-white/10 opacity-50'}`} data-testid={`cms-section-${section.id}`}>
              <div className="flex flex-col gap-0.5">
                <button onClick={() => handleMove(idx, -1)} className="text-[10px] text-[#6B655F] hover:text-[#D4859B] leading-none">&uarr;</button>
                <button onClick={() => handleMove(idx, 1)} className="text-[10px] text-[#6B655F] hover:text-[#D4859B] leading-none">&darr;</button>
              </div>
              <div className="flex-1">
                <span className="text-sm text-[#2C2A29]">{sectionLabels[section.id]?.[lang] || section.id}</span>
              </div>
              <button onClick={() => handleToggle(section.id)} className={`w-10 h-6 rounded-full transition-all flex items-center px-0.5 ${section.enabled ? 'bg-[#C5DB7B]' : 'bg-[#EAE5DF]'}`}>
                <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${section.enabled ? 'translate-x-4' : 'translate-x-0'}`} />
              </button>
            </div>
          ))}
        </div>
        <Button onClick={handleSave} disabled={saving} className="w-full mt-6 bg-[#D4859B] text-white rounded-full shadow-lg btn-glow" data-testid="save-cms-btn">
          {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : null} {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
